import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useFeatures } from '../contexts/FeaturesContext';
import { useView } from '../contexts/ViewContext';
import AdminHeader from '../components/AdminHeader';
import ClientHeader from '../components/ClientHeader';
import NavSidebar from '../components/NavSidebar';
import { usePageMeta } from '../components/DashboardShell';
import KpiCards from '../components/google-analytics/KpiCards';
import SessionsTrendChart from '../components/google-analytics/SessionsTrendChart';
import TrafficAcquisitionTabs from '../components/google-analytics/TrafficAcquisitionTabs';
import ContentBehaviorTabs from '../components/google-analytics/ContentBehaviorTabs';
import WhatChangedCard from '../components/google-analytics/WhatChangedCard';
import IntelligenceCards from '../components/google-analytics/IntelligenceCards';
import { Skeleton, SkeletonChart, SkeletonMetricCard } from '../components/shared/Skeleton';
import { fmtDate } from '../components/shared/format';
import { unwrapItems } from '../components/shared/unwrapItems';
import InfoTip from '../components/shared/InfoTip';
import useGA4Fetch from '../hooks/useGA4Fetch';
import useGA4PaginatedFetch from '../hooks/useGA4PaginatedFetch';
import useGA4Windows from '../hooks/useGA4Windows';
import googleAdsIconDark from '../assets/icons/google-ads-dark.svg';
import googleAdsIconLight from '../assets/icons/google-ads-light.svg';
import {
  getGA4Kpis, getGA4TrafficTrend, getGA4Traffic, getGA4Channels, getGA4ChannelTrend,
  getGA4SourceMedium, getGA4SourceMediumTrend, getGA4Referrals, getGA4ReferralWindows,
  getGA4Campaigns, getGA4PageWindows, getGA4Pages, getGA4LandingPages,
  getGA4GeoWindows, getGA4GeoRegion, getGA4Devices, getGA4BrowserWindows, getGA4Browsers,
  getGA4OSWindows, getGA4OperatingSystems, getGA4Events, getGA4Conversions,
  getGA4ChartUserType, getGA4ChartHourlyTraffic, getGA4ChartHourlyTrafficWindows,
  getGA4ChartDayOfWeek, getGA4ChartDayOfWeekWindows, getGA4LastUpdated,
} from '../api/auth';

/* ----------------------------- Page Skeleton -----------------------------
 * Rendered until the first batch (KPIs + sessions trend) has returned.
 * Mirrors the high-level page layout so the transition to real content
 * doesn't cause a visible reflow.
 */
const GA4PageSkeleton = () => (
  <div className="space-y-4 sm:space-y-6">
    {/* WhatChanged card placeholder */}
    <Skeleton className="h-24 rounded-xl" />
    {/* KPI cards row */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
      {Array.from({ length: 8 }).map((_, i) => <SkeletonMetricCard key={i} />)}
    </div>
    {/* Sessions trend chart */}
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-4 sm:p-5">
      <Skeleton className="h-5 w-40 mb-4" />
      <SkeletonChart height={280} />
    </div>
    {/* Section: Period at a Glance — intelligence cards grid */}
    <div>
      <Skeleton className="h-4 w-44 mb-3" />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-20 rounded-xl" />
        ))}
      </div>
    </div>
    {/* Section: Traffic & Acquisition */}
    <div>
      <Skeleton className="h-4 w-44 mb-3" />
      <Skeleton className="h-[360px] rounded-xl" />
    </div>
    {/* Section: Content & Behavior */}
    <div>
      <Skeleton className="h-4 w-44 mb-3" />
      <Skeleton className="h-[360px] rounded-xl" />
    </div>
  </div>
);

/* ----------------------------- Page Component ----------------------------- */
const GoogleAnalyticsPage = () => {
  usePageMeta({ headerActive: 'Website Analytics', sidebarActive: 'analytics' });
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { hasFeature } = useFeatures();
  const view = useView();
  const username = localStorage.getItem('username') || 'User';
  const isClient = view.isClient;
  const dashboardPath = isClient ? '/client-dashboard' : '/dashboard';

  const selectedClientId = view.supabaseClientId;
  const noClient = !selectedClientId;

  /* ---- Filter state ---- */
  // Date range comes from the global picker in AdminHeader/ClientHeader (via ViewContext)
  // so it stays in sync across pages — no per-page filter here.
  const rangeStart = view.rangeStart;
  const rangeEnd = view.rangeEnd;
  const [comparisonMode, setComparisonMode] = useState("previous_period");
  const noRange = noClient || !rangeStart || !rangeEnd;

  // 2026-07-13 — removed the "override global range to Last 30 Days on
  // first mount" logic. The date picker is global (ViewContext), and every
  // dashboard should honor whatever the user has selected in the header,
  // not stomp it. If a client-specific default is ever needed, add it to
  // ViewContext's default initialization, not here.

  /* ---- Last Updated ---- */
  const { data: lastUpdated } = useGA4Fetch(getGA4LastUpdated, [selectedClientId], {
    skip: noClient,
    transform: (resp) => resp?.data?.extracted_at ?? null,
  });

  /* ===================== KPIs (manual — has error state + comparison param) ===================== */
  const [kpis, setKpis] = useState(null);
  const [kpisLoading, setKpisLoading] = useState(false);
  const [error, setError] = useState('');

  const loadKpis = useCallback(async () => {
    if (noRange) return;
    setKpisLoading(true);
    setError('');
    try {
      const resp = await getGA4Kpis(selectedClientId, rangeStart, rangeEnd, comparisonMode === 'off' ? 'none' : comparisonMode);
      setKpis(resp?.data ?? null);
    } catch (e) {
      setError(e?.message || 'Failed to load analytics KPIs');
      setKpis(null);
    } finally {
      setKpisLoading(false);
    }
  }, [selectedClientId, rangeStart, rangeEnd, comparisonMode, noRange]);

  useEffect(() => { loadKpis(); }, [loadKpis]);

  /* ===================== Traffic Trend (manual — comparison overlay logic) ===================== */
  const [trendData, setTrendData] = useState(null);
  const [trendPrevData, setTrendPrevData] = useState(null);
  const [trendLoading, setTrendLoading] = useState(false);

  const loadTrafficTrend = useCallback(async () => {
    if (noRange) return;
    setTrendLoading(true);
    setTrendData(null);
    setTrendPrevData(null);
    try {
      const resp = await getGA4TrafficTrend(selectedClientId, rangeStart, rangeEnd);
      setTrendData(resp?.data ?? null);

      if (comparisonMode !== 'off') {
        const start = new Date(rangeStart + 'T00:00:00');
        const end = new Date(rangeEnd + 'T00:00:00');
        const daysDiff = Math.round((end - start) / (1000 * 60 * 60 * 24));

        let prevStart, prevEnd;
        if (comparisonMode === 'previous_year') {
          prevStart = new Date(start); prevStart.setFullYear(prevStart.getFullYear() - 1);
          prevEnd = new Date(end); prevEnd.setFullYear(prevEnd.getFullYear() - 1);
        } else {
          prevEnd = new Date(start); prevEnd.setDate(prevEnd.getDate() - 1);
          prevStart = new Date(prevEnd); prevStart.setDate(prevStart.getDate() - daysDiff);
        }

        try {
          const prevResp = await getGA4TrafficTrend(selectedClientId, fmtDate(prevStart), fmtDate(prevEnd));
          setTrendPrevData(prevResp?.data ?? null);
        } catch { setTrendPrevData(null); }
      }
    } catch { setTrendData(null); }
    finally { setTrendLoading(false); }
  }, [selectedClientId, rangeStart, rangeEnd, comparisonMode, noRange]);

  useEffect(() => { loadTrafficTrend(); }, [loadTrafficTrend]);

  // Several GA4 RPCs return an `{ items, window/pagination }` envelope rather than a
  // bare array. useGA4PaginatedFetch unwraps `.items` itself, but useGA4Fetch stores
  // `resp.data` verbatim — so any envelope-returning RPC read through useGA4Fetch needs
  // `unwrapItems` (from shared/unwrapItems), or the consuming panel receives the object
  // and either crashes (`for..of` / spread on a non-iterable) or falls through to
  // "No data" (guards on `.length`). Envelope RPCs: landing pages, geo, browsers, OS,
  // hourly, day-of-week.

  /* ===================== Simple date-range fetches (useGA4Fetch) ===================== */
  const { data: trafficData, loading: trafficLoading }         = useGA4Fetch(getGA4Traffic, [selectedClientId, rangeStart, rangeEnd], { skip: noRange });
  const { data: channelData, loading: channelDataLoading }     = useGA4Fetch(getGA4Channels, [selectedClientId, rangeStart, rangeEnd], { skip: noRange });
  const { data: channelTrendData, loading: channelTrendLoading } = useGA4Fetch(getGA4ChannelTrend, [selectedClientId, rangeStart, rangeEnd], { skip: noRange });
  const { data: sourceMediumTrendData }                        = useGA4Fetch(getGA4SourceMediumTrend, [selectedClientId, rangeStart, rangeEnd], { skip: noRange });
  const { data: landingPagesData, loading: landingPagesLoading } = useGA4Fetch(getGA4LandingPages, [selectedClientId, rangeStart, rangeEnd], { skip: noRange, transform: unwrapItems });
  const { data: devicesData, loading: devicesLoading }         = useGA4Fetch(getGA4Devices, [selectedClientId, rangeStart, rangeEnd], { skip: noRange });
  const { data: conversionsData, loading: conversionsLoading } = useGA4Fetch(getGA4Conversions, [selectedClientId, rangeStart, rangeEnd], { skip: noRange });
  const { data: userTypeData, loading: userTypeLoading }       = useGA4Fetch(getGA4ChartUserType, [selectedClientId, rangeStart, rangeEnd], { skip: noRange });

  const channelLoading = channelDataLoading || channelTrendLoading;

  /* ===================== Paginated date-range fetches (useGA4PaginatedFetch) ===================== */
  const { data: sourceMediumData, loading: sourceMediumPagLoading } = useGA4PaginatedFetch(getGA4SourceMedium, [selectedClientId, rangeStart, rangeEnd], { skip: noRange });
  const { data: campaignsData, loading: campaignsLoading }          = useGA4PaginatedFetch(getGA4Campaigns, [selectedClientId, rangeStart, rangeEnd], { skip: noRange });
  const { data: eventsData, loading: eventsLoading }                = useGA4PaginatedFetch(getGA4Events, [selectedClientId, rangeStart, rangeEnd], { skip: noRange, sortBy: 'event_count' });

  const sourceMediumLoading = sourceMediumPagLoading;

  /* ===================== Window loaders (useGA4Windows) =====================
   * Optimistic guess: backend windows are 7-day rolling, stepping daily, capped at
   * the latest ingested day. We seed `selected` with a guess so downstream data
   * fetches fire in parallel with the windows list. If the guess matches the real
   * latest window, we save 1 RTT; otherwise the hook falls back to `windows[0]`
   * automatically when the list returns.
   */
  const windowGuess = useMemo(() => {
    if (!rangeEnd) return null;
    const todayStr = fmtDate(new Date());
    const cappedEnd = rangeEnd >= todayStr ? todayStr : rangeEnd;
    const end = new Date(cappedEnd + 'T00:00:00');
    // Backend ingestion lags by a day when rangeEnd is today/future.
    if (rangeEnd >= todayStr) end.setDate(end.getDate() - 1);
    const start = new Date(end);
    start.setDate(start.getDate() - 6);
    return { date_start: fmtDate(start), date_end: fmtDate(end) };
  }, [rangeEnd]);

  const winOpts = useMemo(
    () => ({ startDate: rangeStart, endDate: rangeEnd, initialGuess: windowGuess }),
    [rangeStart, rangeEnd, windowGuess]
  );
  const referralWin  = useGA4Windows(getGA4ReferralWindows,             selectedClientId, winOpts);
  const pageWin      = useGA4Windows(getGA4PageWindows,                 selectedClientId, winOpts);
  const geoWin       = useGA4Windows(getGA4GeoWindows,                  selectedClientId, winOpts);
  const browserWin   = useGA4Windows(getGA4BrowserWindows,              selectedClientId, winOpts);
  const osWin        = useGA4Windows(getGA4OSWindows,                   selectedClientId, winOpts);
  const hourlyWin    = useGA4Windows(getGA4ChartHourlyTrafficWindows,   selectedClientId, winOpts);
  const dowWin       = useGA4Windows(getGA4ChartDayOfWeekWindows,       selectedClientId, winOpts);

  /* ===================== Window-driven data fetches ===================== */
  const { data: referralsData, loading: referralsFetchLoading }         = useGA4PaginatedFetch(getGA4Referrals, [selectedClientId, referralWin.selected?.date_start, referralWin.selected?.date_end], { skip: !referralWin.selected });
  const { data: pagesData, loading: pagesFetchLoading }                 = useGA4PaginatedFetch(getGA4Pages, [selectedClientId, pageWin.selected?.date_start, pageWin.selected?.date_end], { skip: !pageWin.selected, sortBy: 'pageviews' });
  // geo/browsers/OS/hourly/day-of-week all return the same `{ items, window }`
  // envelope — unwrap via the shared `unwrapItems` defined above.
  const { data: geoData, loading: geoFetchLoading }                     = useGA4Fetch(getGA4GeoRegion, [selectedClientId, geoWin.selected?.date_start, geoWin.selected?.date_end], { skip: !geoWin.selected, transform: unwrapItems });
  const { data: browsersData, loading: browsersFetchLoading }           = useGA4Fetch(getGA4Browsers, [selectedClientId, browserWin.selected?.date_start, browserWin.selected?.date_end], { skip: !browserWin.selected, transform: unwrapItems });
  const { data: osData, loading: osFetchLoading }                       = useGA4Fetch(getGA4OperatingSystems, [selectedClientId, osWin.selected?.date_start, osWin.selected?.date_end], { skip: !osWin.selected, transform: unwrapItems });
  const { data: hourlyTrafficData, loading: hourlyTrafficFetchLoading } = useGA4Fetch(getGA4ChartHourlyTraffic, [selectedClientId, hourlyWin.selected?.date_start, hourlyWin.selected?.date_end], { skip: !hourlyWin.selected, transform: unwrapItems });
  const { data: dayOfWeekData, loading: dayOfWeekFetchLoading }         = useGA4Fetch(getGA4ChartDayOfWeek, [selectedClientId, dowWin.selected?.date_start, dowWin.selected?.date_end], { skip: !dowWin.selected, transform: unwrapItems });

  const referralsLoading     = referralWin.loading || referralsFetchLoading;
  const pagesLoading         = pageWin.loading     || pagesFetchLoading;
  const geoLoading           = geoWin.loading      || geoFetchLoading;
  const browsersLoading      = browserWin.loading  || browsersFetchLoading;
  const osLoading            = osWin.loading       || osFetchLoading;
  const hourlyTrafficLoading = hourlyWin.loading   || hourlyTrafficFetchLoading;
  const dayOfWeekLoading     = dowWin.loading      || dayOfWeekFetchLoading;

  // First-load gate: keep the page in a unified skeleton until the KPI payload
  // lands. KPIs are typically the fastest fetch, so gating on them alone gives
  // the "everything loads together" feel without making the page wait for the
  // slowest fetch — the sessions trend (and other sections) finish their own
  // per-section skeletons as they arrive. Once `kpis` is set, subsequent
  // refetches (e.g. date-range changes) don't re-trigger the gate because the
  // prior payload stays in state.
  const firstBatchLoading = kpisLoading && !kpis;

  /* ===================== Geo Trend (manual — loads all windows in parallel) ===================== */
  const [geoTrendData, setGeoTrendData] = useState(null);
  const [geoTrendLoading, setGeoTrendLoading] = useState(false);

  const loadGeoTrendData = useCallback(async () => {
    if (!selectedClientId || !geoWin.windows || geoWin.windows.length < 2) {
      setGeoTrendData(null);
      return;
    }
    setGeoTrendLoading(true);
    try {
      const results = await Promise.all(
        geoWin.windows.map(async (w) => {
          try {
            const resp = await getGA4GeoRegion(selectedClientId, w.date_start, w.date_end);
            // Same `{ items, window }` envelope as the single-window geo fetch —
            // unwrap so GeoTrendChart can iterate each window's rows.
            return { window: w, data: unwrapItems(resp) };
          } catch { return { window: w, data: [] }; }
        })
      );
      setGeoTrendData(results);
    } catch { setGeoTrendData(null); }
    finally { setGeoTrendLoading(false); }
  }, [selectedClientId, geoWin.windows]);

  useEffect(() => { loadGeoTrendData(); }, [loadGeoTrendData]);

  /* ---- Glance snapshot note — explains the 7-day window used by Top Referral, Homepage, Peak Hour ---- */
  const glanceSnapshotNote = useMemo(() => {
    const w = referralWin.selected || pageWin.selected || hourlyWin.selected;
    if (!w?.date_start || !w?.date_end) return '';
    const fmtShort = (iso) => new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `Top Referral, Homepage & Peak Hour reflect a 7-day snapshot: ${fmtShort(w.date_start)} – ${fmtShort(w.date_end)}`;
  }, [referralWin.selected, pageWin.selected, hourlyWin.selected]);

  /* ---- Comparison label ---- */
  const getComparisonLabel = () => {
    if (comparisonMode === "off") return null;
    const start = new Date(rangeStart + "T00:00:00");
    const end = new Date(rangeEnd + "T00:00:00");
    const daysDiff = Math.round((end - start) / (1000 * 60 * 60 * 24));
    const fmt = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

    if (comparisonMode === "previous_period") {
      const compEnd = new Date(start); compEnd.setDate(compEnd.getDate() - 1);
      const compStart = new Date(compEnd); compStart.setDate(compStart.getDate() - daysDiff);
      return `Comparing ${fmt(start)} – ${fmt(end)} vs ${fmt(compStart)} – ${fmt(compEnd)}`;
    }
    if (comparisonMode === "previous_year") {
      const compStart = new Date(start); compStart.setFullYear(compStart.getFullYear() - 1);
      const compEnd = new Date(end); compEnd.setFullYear(compEnd.getFullYear() - 1);
      return `Comparing ${fmt(start)} – ${fmt(end)} vs ${fmt(compStart)} – ${fmt(compEnd)}`;
    }
    return null;
  };

  /* ===================== RENDER ===================== */
  return (
    <main className="flex-1 min-w-0 px-4 md:px-6 lg:px-10 py-4 sm:py-6 space-y-4 sm:space-y-6">
          <div className="flex flex-wrap items-baseline justify-between gap-2 max-[344px]:flex-col max-[344px]:items-start">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100 max-[344px]:w-full">Website Analytics</h1>
            {lastUpdated && (
              <span className="text-[11px] text-gray-400 dark:text-gray-500">
                Last updated: {new Date(lastUpdated).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} at {new Date(lastUpdated).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
              </span>
            )}
          </div>

          {/* Error state */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 text-red-700 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Empty state */}
          {kpis && !kpis.sessions && !kpisLoading && (
            <div className="bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 text-gray-500 dark:text-gray-400 text-sm text-center">
              No analytics data for this period.
            </div>
          )}

          {firstBatchLoading ? <GA4PageSkeleton /> : (<>
          {/* ============ "WHAT CHANGED" AI SUMMARY + ALERTS ============ */}
          <WhatChangedCard
            kpis={kpis}
            comparisonMode={comparisonMode}
            channelData={channelData}
            devicesData={devicesData}
            clientId={selectedClientId}
            startDate={rangeStart}
            endDate={rangeEnd}
          />

          {/* ============ KPI CARDS ============ */}
          <KpiCards
            kpis={kpis}
            loading={kpisLoading}
            comparisonMode={comparisonMode}
            setComparisonMode={setComparisonMode}
            getComparisonLabel={getComparisonLabel}
            trendData={trendData}
          />

          {/* ============ SESSIONS TREND CHART ============ */}
          <SessionsTrendChart
            data={trendData}
            previousData={trendPrevData}
            comparisonMode={comparisonMode}
            loading={trendLoading}
          />

          {/* ============ PERIOD AT A GLANCE ============ */}
          <div>
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Period at a Glance</h2>
              <InfoTip text="High-signal callouts pulled from the data this period: top channel, top referral, busiest hour, and homepage share. The 7-day snapshot note shows the analysis window for window-based fields." />
              {glanceSnapshotNote && (
                <span className="text-[11px] text-gray-400 dark:text-gray-500 normal-case tracking-normal">— {glanceSnapshotNote}</span>
              )}
            </div>
            <IntelligenceCards
              channelData={channelData}
              devicesData={devicesData}
              dayOfWeekData={dayOfWeekData}
              pagesData={pagesData}
              kpis={kpis}
              hourlyTrafficData={hourlyTrafficData}
              referralsData={referralsData}
              loading={kpisLoading || channelLoading || devicesLoading || pagesLoading || hourlyTrafficLoading || referralsLoading || dayOfWeekLoading}
            />
          </div>

          {/* ============ SECTION 1: TRAFFIC & ACQUISITION ============ */}
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3 flex items-center">
              <span>Traffic & Acquisition</span>
              <InfoTip text="Where your traffic comes from and how it behaves once it arrives — channels, source/medium, campaigns, events, and device split." />
            </h2>
            <TrafficAcquisitionTabs
              trafficData={trafficData}
              trafficLoading={trafficLoading}
              channelData={channelData}
              channelLoading={channelLoading}
              channelTrendData={channelTrendData}
              sourceMediumData={sourceMediumData}
              sourceMediumLoading={sourceMediumLoading}
              sourceMediumTrendData={sourceMediumTrendData}
              campaignsData={campaignsData}
              campaignsLoading={campaignsLoading}
              eventsData={eventsData}
              eventsLoading={eventsLoading}
              conversionsData={conversionsData}
              conversionsLoading={conversionsLoading}
              selectedClientId={selectedClientId}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              hourlyTrafficData={hourlyTrafficData}
              hourlyTrafficLoading={hourlyTrafficLoading}
              hourlyTrafficWindows={hourlyWin.windows}
              selectedHourlyTrafficWindow={hourlyWin.selected}
              onHourlyTrafficWindowChange={hourlyWin.setSelected}
              dayOfWeekData={dayOfWeekData}
              dayOfWeekLoading={dayOfWeekLoading}
              dayOfWeekWindows={dowWin.windows}
              selectedDayOfWeekWindow={dowWin.selected}
              onDayOfWeekWindowChange={dowWin.setSelected}
              devicesData={devicesData}
              devicesLoading={devicesLoading}
              browsersData={browsersData}
              browsersLoading={browsersLoading}
              browserWindows={browserWin.windows}
              selectedBrowserWindow={browserWin.selected}
              onBrowserWindowChange={browserWin.setSelected}
              osData={osData}
              osLoading={osLoading}
              osWindows={osWin.windows}
              selectedOsWindow={osWin.selected}
              onOsWindowChange={osWin.setSelected}
              userTypeData={userTypeData}
              userTypeLoading={userTypeLoading}
            />
          </div>

          {/* ============ SECTION 2: CONTENT & BEHAVIOR ============ */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Content & Behavior</h2>
              <InfoTip text="What visitors do on the site — referral sources, top pages, and where they're located. Uses pre-computed 7-day windows for performance." />
              <span className="text-xs text-gray-400 dark:text-gray-500">— based on pre-computed analysis windows</span>
            </div>
            <ContentBehaviorTabs
              referralsData={referralsData}
              referralsLoading={referralsLoading}
              referralWindows={referralWin.windows}
              selectedReferralWindow={referralWin.selected}
              onReferralWindowChange={referralWin.setSelected}
              pagesData={pagesData}
              pagesLoading={pagesLoading}
              pageWindows={pageWin.windows}
              selectedPageWindow={pageWin.selected}
              onPageWindowChange={pageWin.setSelected}
              landingPagesData={landingPagesData}
              landingPagesLoading={landingPagesLoading}
              geoData={geoData}
              geoLoading={geoLoading}
              geoWindows={geoWin.windows}
              geoTrendData={geoTrendData}
              geoTrendLoading={geoTrendLoading}
              selectedGeoWindow={geoWin.selected}
              onGeoWindowChange={geoWin.setSelected}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              selectedClientId={selectedClientId}
            />
          </div>
          </>)}

        </main>
  );
};

export default GoogleAnalyticsPage;
